import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface PipeSelectorOption {
  value: string;
  label: string;
}

interface PipeSelectorProps {
  value: string | null;
  options: PipeSelectorOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  triggerWidth?: string;
  /**
   * Show a search input inside the popover. When undefined, search auto-enables
   * once there are more than 10 options. Pass `false` to force-disable.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

const SEARCH_AUTO_THRESHOLD = 10;

export const PipeSelector: React.FC<PipeSelectorProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select pipe',
  className,
  triggerWidth = 'w-[400px]',
  searchable,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Resolve effective search-on/off: explicit prop wins, else auto-enable when many options.
  const showSearch = searchable ?? options.length > SEARCH_AUTO_THRESHOLD;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  // Reset query each time the popover opens; auto-focus the search input when present.
  useEffect(() => {
    if (open) {
      setQuery('');
      if (showSearch) {
        // Defer focus so the input has rendered.
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }
  }, [open, showSearch]);

  const selected = options.find((o) => o.value === value);

  const filteredOptions = useMemo(() => {
    if (!showSearch || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((opt) => {
      // Always keep the currently selected option visible, matching SearchableSelect's behavior.
      if (opt.value === value) return true;
      return opt.label.toLowerCase().includes(q);
    });
  }, [options, query, showSearch, value]);

  return (
    <div ref={wrapperRef} className={cn('relative inline-block', triggerWidth, className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex h-10 items-center justify-between rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px] text-text-grey transition-colors',
          'hover:border-heritage-grey focus:outline-none focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red',
          open && 'border-hero-red ring-2 ring-hero-red/30'
        )}
      >
        <span className={cn('truncate text-left', !selected && 'text-heritage-grey')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-heritage-grey transition-transform flex-shrink-0 ml-2', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border border-[#E2E5EA] bg-white shadow-lg"
          role="listbox"
        >
          {showSearch && (
            <div className="p-2 border-b border-[#EEF0F3]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-heritage-grey" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-7 pr-3 py-1.5 text-[11px] text-text-grey placeholder:text-heritage-grey border border-[#E2E5EA] rounded-md focus:outline-none focus:border-blueprint-blue"
                />
              </div>
            </div>
          )}

          <div className="max-h-72 overflow-y-auto py-1">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-[12px] text-heritage-grey">No options</div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-[12px] text-heritage-grey">{emptyMessage}</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition-colors',
                      'hover:bg-page-bg',
                      isSelected ? 'text-blueprint-blue font-medium bg-page-bg' : 'text-text-grey'
                    )}
                  >
                    <Check className={cn('h-4 w-4 flex-shrink-0', isSelected ? 'text-hero-red' : 'opacity-0')} />
                    <span className="truncate">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
